import React, { useState } from 'react';
import { Calendar, Users, BookOpen, GraduationCap, ArrowRight } from 'lucide-react';
import { activities } from '../data/mock';
import activitesHero from "../assets/images/activitiesBG.webp";

const Activities = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="gradient-hero-saffron py-20"
        style={{
          backgroundImage: `
      linear-gradient(to bottom, rgba(255,247,230,0.85), rgba(116, 75, 37, 0.8)),
      url('${activitesHero}')
    `,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground font-serif">
              Activities & Events
            </h1>
            <p className="text-xl text-muted-foreground">
              Engaging programs fostering learning and community
            </p>
          </div>
        </div>
      </section>

      {/* Activity Categories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {['all', 'meetings', 'workshops', 'lectures', 'reading'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-md font-medium text-sm transition-all ${activeTab === tab
                    ? 'bg-primary text-white shadow'
                    : 'bg-secondary/20 text-foreground hover:bg-primary/10'
                    }`}
                >
                  {tab === 'all' ? 'All Activities' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Seminars & Conferences */}
            {(activeTab === 'all' || activeTab === 'meetings') && (
              <div className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground font-serif">
                    Meetings & Seminars</h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activities[0].items.map((item, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-white to-secondary/20 rounded-xl shadow-lg overflow-hidden hover-lift border border-primary/10"
                    >
                      {/* Image */}
                      {item.image && (
                        <div
                          className="h-48 w-full overflow-hidden cursor-pointer relative group"
                          onClick={() => setLightboxImage(item.image)}
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-green text-sm">Click to view</span>
                          </div>
                        </div>

                      )}

                      {/* Content */}
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-primary font-semibold mb-3">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">{item.date}</span>
                        </div>

                        <h3 className="text-xl font-bold text-foreground mb-3">
                          {item.title}
                        </h3>

                        <p className="text-muted-foreground leading-relaxed mb-4">
                          {item.description}
                        </p>

                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-semibold flex items-center gap-2 hover:gap-4 transition-all"
                        >
                          Learn More
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>

                  ))}
                </div>
              </div>
            )}

            {/* Workshops */}
            {(activeTab === 'all' || activeTab === 'workshops') && (
              <div className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground font-serif">
                    Workshops
                  </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {activities[1].items.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover-lift"
                    >
                      <div className="h-40 bg-gradient-to-br from-primary/20 to-orange-400/20 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                          <GraduationCap className="w-8 h-8 text-primary" />
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
                          {item.duration}
                        </div>
                        <h3 className="text-lg font-bold text-foreground mb-3">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Public Lectures */}
            {(activeTab === 'all' || activeTab === 'lectures') && (
              <div className="mb-16">
                <div className="bg-gradient-to-br from-secondary/30 to-white rounded-2xl p-8 shadow-lg border border-primary/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-3xl font-bold text-foreground font-serif">
                      Public Lectures
                    </h2>
                  </div>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {activities[2].description}
                  </p>
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    <img
                      src={'/assets/interns-meeting.webp'}
                      alt="Interns Meeting"
                      className="rounded-lg h-80 w-full object-cover"
                    />
                    <img
                      src={'/assets/event2.jpeg'}
                      alt="Interns Meeting"
                      className="rounded-lg h-80 w-full object-cover"
                    />
                    <img
                      src={'/assets/media.jpeg'}
                      alt="Interns in Media"
                      className="rounded-lg h-80 w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Reading Circle */}
            {(activeTab === 'all' || activeTab === 'reading') && (
              <div>
                <div className="bg-gradient-to-br from-primary/10 to-orange-400/10 rounded-2xl p-8 shadow-lg border-2 border-primary/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-md">
                      <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-3xl font-bold text-foreground font-serif">
                      Reading Circle – Vimarśa
                    </h2>
                  </div>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                    {activities[3].description}
                  </p>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6">
                    <h3 className="font-bold text-foreground mb-3">Join Our Next Session</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      A vibrant forum where students and faculty engage in critical dialogue with classical texts, fostering deep intellectual exchange and collaborative learning.
                    </p>
                    <button className="btn-saffron">
                      Register Interest
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FDP & SDP */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Faculty & Student Development Programmes
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Regular training programs to enhance academic capacity, foster innovation in teaching-learning, and ensure effective implementation of IKS-based education.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-gradient-to-br from-secondary/30 to-white px-6 py-3 rounded-full border border-primary/20">
                  <span className="font-semibold text-foreground">Curriculum Design</span>
                </div>
                <div className="bg-gradient-to-br from-secondary/30 to-white px-6 py-3 rounded-full border border-primary/20">
                  <span className="font-semibold text-foreground">Research Methods</span>
                </div>
                <div className="bg-gradient-to-br from-secondary/30 to-white px-6 py-3 rounded-full border border-primary/20">
                  <span className="font-semibold text-foreground">Pedagogy Innovation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold font-serif">
              Stay Updated on Our Events
            </h2>
            <p className="text-xl text-white/90">
              Subscribe to our newsletter for the latest announcements
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-full text-foreground focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-primary font-bold px-8 py-3 rounded-full hover:bg-gray-100 transition-all">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 🔍 Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setLightboxImage(null)}
        >
          <div
            className="relative max-w-4xl w-full px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300"
            >
              ×
            </button>

            {/* Image */}
            <img
              src={lightboxImage}
              alt="Preview"
              className="w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Activities;
